import styled, { keyframes, useTheme } from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import {
  Heading3Text,
  ParagraphLargeText,
  BodyLarge,
} from '../../../components/Texts';
import WidthContainer from '../../../components/WidthContainer';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import Button from '../../../components/Button';
import GiftCard from '../../../components/GiftCard';
import Particles from './Particles';
import { useImagePreload } from '../../../common/images';
import StoreButton from '../../../components/StoreButton';
import { useFlag } from '@unleash/proxy-client-react';
import { Capacitor } from '@capacitor/core';

// Exciting entrance animations
const giftCardEntrance = keyframes`
  0% {
    transform: scale(0.3) rotateY(-15deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) rotateY(5deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(1) rotateY(0deg);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const ClaimGiftContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  margin-top: 50px;
  gap: 20px;
  z-index: 1;
  max-width: 516px;
  margin-left: auto;
  margin-right: auto;
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 15px;
    gap: 0px;
  }
`;

const GiftContainer = styled.div`
  text-align: start;
  align-items: start;
  width: 100%;
  position: relative;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;

  @media ${PHONE_MEDIA_QUERY} {
    margin-bottom: 20px;
  }
`;

const explodeAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
`;

const GiftCardWrapper = styled.div`
  width: 100%;
  max-width: 516px;
  border-radius: 15px;
  overflow: visible;
  opacity: 1;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 407px;
  }
`;

const CardAnimationWrapper = styled.div`
  animation: ${giftCardEntrance} 1.2s ease-out forwards;
  animation-delay: 0.5s;
  animation-fill-mode: both;
  box-shadow: 0 0 80px rgba(38, 60, 208, 0.25);
  border-radius: 15px;
`;

const appearAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(0.5);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const wrapperAnimationMap = {
  reveal: appearAnimation,
  explode: explodeAnimation,
};

const CardExplosionAnimationWrapper = styled.div`
  animation: ${({ animationType }) => wrapperAnimationMap[animationType]} 0.25s
    ease-in-out forwards;
  box-shadow: 0 0 80px rgba(38, 60, 208, 0.25);
  border-radius: 15px;
`;

const CardDetailRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.8s;
`;

const MessageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.9s;
`;

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: ${({ animated }) => (animated ? '1.7s' : '.5s')};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 8px;
    max-width: unset;
  }
`;

const StyledHeading3Text = styled(Heading3Text)`
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.2s;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 24px;
  }
`;

const StyledParagraphLargeText = styled(ParagraphLargeText)`
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: 0.4s;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-top: 20px;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-fill-mode: both;
  animation-delay: ${({ animated }) => (animated ? '1.7s' : '.5s')};

  @media ${PHONE_MEDIA_QUERY} {
    gap: 0px;
  }
`;

const shakeAnimation = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
  5% {
    transform: translate(-1px, -0.5px) rotate(-0.5deg) scale(1.01);
  }
  10% {
    transform: translate(1.5px, 0.5px) rotate(0.4deg) scale(1.02);
  }
  15% {
    transform: translate(-0.5px, 1.2px) rotate(-0.7deg) scale(1.03);
  }
  20% {
    transform: translate(0.8px, -0.7px) rotate(0.6deg) scale(1.03);
  }
  25% {
    transform: translate(-1.2px, 0.6px) rotate(-0.9deg) scale(1.04);
  }
  30% {
    transform: translate(1px, -1.3px) rotate(0.8deg) scale(1.05);
  }
  35% {
    transform: translate(-0.7px, 0.9px) rotate(-1.1deg) scale(1.06);
  }
  40% {
    transform: translate(0.9px, 1.3px) rotate(0.9deg) scale(1.07);
  }
  45% {
    transform: translate(-1.4px, -0.8px) rotate(-1.2deg) scale(1.08);
  }
  50% {
    transform: translate(1.3px, 0.7px) rotate(1.1deg) scale(1.09);
  }
  55% {
    transform: translate(-1.8px, 1.1px) rotate(-1.3deg) scale(1.1);
  }
  60% {
    transform: translate(1.6px, -1.2px) rotate(1.2deg) scale(1.11);
  }
  65% {
    transform: translate(-1.2px, 1.6px) rotate(-1.5deg) scale(1.12);
  }
  70% {
    transform: translate(1.5px, -1.6px) rotate(1.4deg) scale(1.13);
  }
  75% {
    transform: translate(-2px, 1.2px) rotate(-1.7deg) scale(1.14);
  }
  80% {
    transform: translate(2.1px, -1.4px) rotate(1.6deg) scale(1.15);
  }
  85% {
    transform: translate(-1.7px, 2px) rotate(-1.9deg) scale(1.16);
  }
  90% {
    transform: translate(2.2px, -2px) rotate(2deg) scale(1.18);
  }
  95% {
    transform: translate(-2.3px, 1.8px) rotate(-2.1deg) scale(1.19);
  }
  100% {
    transform: translate(2.5px, -1.7px) rotate(2.2deg) scale(1.2);
  }
`;

const occasionalMovement = keyframes`
  0%, 95%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  53% {
    transform: translate(1px, -0.5px) rotate(0.6deg);
  }
  54% {
    transform: translate(-0.5px, 0.5px) rotate(-0.5deg);
  }
  55% {
    transform: translate(0.5px, 0) rotate(0.3deg);
  }
  56% {
    transform: translate(0, 0) rotate(0deg);
  }
  96% {
    transform: translate(1px, -0.5px) rotate(0.6deg);
  }
  97% {
    transform: translate(-0.5px, 0.5px) rotate(-0.5deg);
  }
  98% {
    transform: translate(0.5px, 0) rotate(0.3deg);
  }
  99% {
    transform: translate(0, 0) rotate(0deg);
  }
`;

const animations = {
  shake: shakeAnimation,
  explode: explodeAnimation,
  occasional: occasionalMovement,
};

const HiddenGiftCard = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
  overflow: hidden;
  animation: ${({ animationType }) => animations[animationType]}
    ${({ animationDuration }) => animationDuration} ease-in-out
    ${({ animationType }) =>
      animationType === 'occasional' ? 'infinite' : 'infinite'};
`;

const ClickableWrapper = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
  user-select: none;
`;

const fadeInLabel = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const ClickToRevealLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  pointer-events: none;
  animation: ${fadeInLabel} 0.6s ease-out forwards;
  animation-delay: 0.3s;
  opacity: 0;
  backdrop-filter: blur(4px);
  text-align: center;
  white-space: nowrap;

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
    padding: 10px 20px;
  }
`;

export const WRAPPING_TYPE_MAP = {
  default: '/hidden-gift.webp',
  christmas: '/christmas-gift-card.webp',
  halloween: '/halloween-gift-card.webp',
  breakpoint: '/breakpoint-gift-card.webp',
};

const PresentationalGift = ({
  usdAmount,
  tokenSymbol,
  tokenAmount,
  token,
  giftCardImageUrl,
  onClick,
  email,
  message,
  buttonText = 'Claim Gift',
  status,
  htlcExpiry,
  showStatus = false,
  animated = false,
  giftId,
  wrappingType = 'default',
  isCreator = false,
  isReceiver = false,
}) => {
  const isClaimed = ['HTLC_CLAIMED', 'CLAIMED'].includes(status);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const [animationStarted, setAnimationStarted] = useState(false);
  const theme = useTheme();
  const useHalloweenWrapping = useFlag('useHalloweenWrapping');
  const showAppDownload = useFlag('showAppDownload');

  const shouldAnimate = animated && !isClaimed && animationStarted;

  const [revelead, setRevealed] = useState(false);
  const [explotion, setExplotion] = useState(false);
  const [cardReference, setCardReference] = useState(null);
  const [animationPosition, setAnimationPosition] = useState({
    x: 0,
    y: 0,
  });
  const [userClickedReveal, setUserClickedReveal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (shouldAnimate) {
      setAnimationStarted(true);
      setTimeout(() => {
        setExplotion(true);
        setTimeout(() => {
          localStorage.setItem(`gift-${giftId}`, 'true');
          setRevealed(true);
        }, 200);
      }, 1500);
    }
  }, [shouldAnimate, giftCardImageUrl]);

  const handleRevealClick = () => {
    if (revelead || isAnimating) return;

    setUserClickedReveal(true);
    setIsAnimating(true);

    // Play sound
    if (audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.warn('Failed to play sound:', error);
      });
    }

    // Trigger the same animation sequence as shouldAnimate
    setAnimationStarted(true);
    setTimeout(() => {
      setExplotion(true);
      setTimeout(() => {
        localStorage.setItem(`gift-${giftId}`, 'true');
        setRevealed(true);
        setIsAnimating(false);
      }, 200);
    }, 1500);
  };

  useEffect(() => {
    useImagePreload(giftCardImageUrl);
  }, [giftCardImageUrl]);

  useEffect(() => {
    // Initialize audio element
    audioRef.current = new Audio('/sounds/revealGift.mp3');
    audioRef.current.volume = 0.7;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Show label after image is loaded with a delay
    if (imageLoaded && !userClickedReveal && !shouldAnimate && !revelead) {
      const timer = setTimeout(() => {
        setShowLabel(true);
      }, 500); // 500ms delay after image loads

      return () => clearTimeout(timer);
    } else {
      setShowLabel(false);
    }
  }, [imageLoaded, userClickedReveal, shouldAnimate, revelead]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  useEffect(() => {
    if (cardReference) {
      if (animationPosition.x === 0 || animationPosition.y === 0) {
        const giftCardRect = cardReference?.getBoundingClientRect();
        if (giftCardRect.width) {
          const explosionX = giftCardRect.left + giftCardRect.width / 2;
          const explosionY = giftCardRect.top + (isMobile ? 200 : 286) / 2;
          setAnimationPosition({
            x: explosionX,
            y: explosionY,
          });
        }
      }
    }
  }, [cardReference]);

  const CardAWrapper =
    shouldAnimate || userClickedReveal
      ? CardExplosionAnimationWrapper
      : CardAnimationWrapper;

  return (
    <>
      {revelead && (shouldAnimate || userClickedReveal) && (
        <Particles
          x={animationPosition.x}
          y={animationPosition.y}
          isHalloween={useHalloweenWrapping || wrappingType === 'halloween'}
        />
      )}
      <WidthContainer>
        <TitleContainer animated={shouldAnimate}>
          {revelead ? (
            <>
              <StyledHeading3Text
                key='revealed-title'
                style={{ color: theme.text.title }}
              >
                {isClaimed
                  ? isReceiver
                    ? "You've claimed your gift"
                    : 'This gift has been claimed'
                  : "You've been gifted"}
              </StyledHeading3Text>
              {!isClaimed && !isCreator && (
                <StyledHeading3Text style={{ color: theme.text.title }}>
                  <span style={{ fontWeight: 600 }}>
                    ${usdAmount} in {tokenSymbol}!
                  </span>
                </StyledHeading3Text>
              )}
              <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                {isClaimed
                  ? isReceiver
                    ? 'This gift is now yours! You can hold it, trade it, or watch it grow.'
                    : 'This gift has been claimed, but there are plenty more available!'
                  : "Here's your first piece of a growing portfolio. Hold it, trade it, or watch it grow."}
              </StyledParagraphLargeText>
            </>
          ) : (
            <>
              <StyledHeading3Text
                key='secret-title'
                style={{ color: theme.text.title }}
              >
                {wrappingType === 'breakpoint'
                  ? "You've received a Breakpoint limited edition gift!"
                  : "You've received a gift!"}
              </StyledHeading3Text>
              <StyledParagraphLargeText style={{ fontWeight: 400 }}>
                Click the gift card to reveal it.
              </StyledParagraphLargeText>
            </>
          )}
        </TitleContainer>
        <ClaimGiftContainer>
          <GiftContainer>
            <GiftCardWrapper animated={shouldAnimate || userClickedReveal}>
              {revelead ? (
                <CardAWrapper
                  key='revelead'
                  animated={shouldAnimate || userClickedReveal}
                  animationType='reveal'
                >
                  <GiftCard
                    tokenAmount={tokenAmount}
                    usdAmount={usdAmount}
                    token={token}
                    cardImage={giftCardImageUrl}
                  />
                </CardAWrapper>
              ) : (
                <ClickableWrapper onClick={handleRevealClick}>
                  <CardAWrapper
                    key='hidden'
                    animated={shouldAnimate || userClickedReveal}
                    animationType={explotion ? 'explode' : 'reveal'}
                  >
                    <HiddenGiftCard
                      ref={setCardReference}
                      onLoad={handleImageLoad}
                      animationType={
                        explotion
                          ? 'explode'
                          : shouldAnimate || userClickedReveal
                            ? 'shake'
                            : 'occasional'
                      }
                      animationDuration={
                        explotion
                          ? '0.2s'
                          : shouldAnimate || userClickedReveal
                            ? '1.5s'
                            : '6s'
                      }
                      src={
                        WRAPPING_TYPE_MAP[
                          useHalloweenWrapping ? 'halloween' : wrappingType
                        ]
                      }
                    />
                  </CardAWrapper>
                  {showLabel && !userClickedReveal && !shouldAnimate && (
                    <ClickToRevealLabel>Click to reveal</ClickToRevealLabel>
                  )}
                </ClickableWrapper>
              )}
            </GiftCardWrapper>
          </GiftContainer>
          {userClickedReveal && (
            <>
              {email && (
                <CardDetailRow>
                  <BodyLarge $variant='text-secondary'>Gift from:</BodyLarge>
                  <BodyLarge>{email}</BodyLarge>
                </CardDetailRow>
              )}
              {message && (
                <MessageContainer>
                  <BodyLarge $variant='text-secondary'>Message:</BodyLarge>
                  <BodyLarge>{message}</BodyLarge>
                </MessageContainer>
              )}
              {showAppDownload && isMobile && !Capacitor.isNativePlatform() ? (
                <>
                  <StoreButton />
                  <BodyLarge $variant='text-secondary'>
                    Get your gift in our app for the best experience. <br />
                    Prefer not to install?{' '}
                    <span
                      onClick={onClick}
                      style={{
                        textDecoration: 'underline',
                        color: theme.primary.primary,
                      }}
                    >
                      Continue in the browser
                    </span>
                  </BodyLarge>
                </>
              ) : (
                <StyledButton
                  onClick={onClick}
                  fullWidth={isMobile}
                  animated={shouldAnimate}
                  clickAnimation='bouncy'
                >
                  {isClaimed
                    ? isReceiver
                      ? 'View Portfolio'
                      : 'Find Available Gifts'
                    : buttonText}
                </StyledButton>
              )}
            </>
          )}
        </ClaimGiftContainer>
      </WidthContainer>
    </>
  );
};

export default PresentationalGift;
